import { Typography, Link, Stack } from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';

import { useLogger } from '@/hooks/useLogger';
import { useTranslationPrefix } from '@/translations/i18n';

export const DEFAULT_MAX_FILE_COUNT = 1;
export const DEFAULT_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 Mb

export interface FilePickerRef {
  openPickerDialog: () => void;
}

export interface FilePickerProps {
  maxFileCount?: number;
  maxFileSizeInBytes?: number;
  withDropZone?: boolean;
  showDropZoneBorder?: boolean;
  highlightOnDrag?: boolean;
  dropZoneBorderRadius?: number | string;
  onChange?: (files: File[], fileRejections: FileRejection[]) => void;
}

export const FilePicker = forwardRef<FilePickerRef, FilePickerProps>(
  (
    {
      maxFileCount = DEFAULT_MAX_FILE_COUNT,
      maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
      withDropZone,
      showDropZoneBorder = true,
      highlightOnDrag = false,
      dropZoneBorderRadius = 6.25,
      onChange,
    },
    ref,
  ) => {
    const t = useTranslationPrefix('drag_and_drop');
    const tCommon = useTranslationPrefix('common');
    const logger = useLogger('FilePicker');

    const onDrop = useCallback(
      (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        logger.debug(
          `onDrop, acceptedFiles: ${JSON.stringify(
            acceptedFiles,
          )}, fileRejections: ${JSON.stringify(fileRejections)}`,
        );

        try {
          onChange?.(acceptedFiles, fileRejections);
        } catch (error) {
          logger.error('Error dropping file:', { error });
        }
      },
      [onChange],
    );

    const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
      accept: {
        'application/pdf': ['.pdf'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
      },
      maxFiles: maxFileCount,
      maxSize: maxFileSizeInBytes,
      noDrag: !withDropZone,
      onDrop,
    });

    useImperativeHandle(ref, () => ({
      openPickerDialog: () => {
        if (!inputRef.current) {
          return;
        }
        inputRef.current.value = '';
        inputRef.current.click();
      },
    }));

    if (withDropZone) {
      return (
        <Stack
          {...getRootProps()}
          sx={{
            width: '100%',
            height: 120,
            borderWidth: showDropZoneBorder ? 1 : 0,
            borderStyle: showDropZoneBorder ? 'dashed' : 'none',
            borderColor:
              showDropZoneBorder && highlightOnDrag && isDragActive
                ? '#c0ff02'
                : showDropZoneBorder
                ? 'background.default'
                : 'transparent',
            borderRadius: dropZoneBorderRadius,
            p: 4,
            backgroundColor:
              highlightOnDrag && isDragActive ? 'rgba(192,255,2,0.045)' : 'transparent',
            boxShadow: highlightOnDrag && isDragActive ? '0 0 22px rgba(192,255,2,0.10)' : 'none',
            flexGrow: 1,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
            cursor: 'pointer',
            transition:
              'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
            ':hover': showDropZoneBorder
              ? { borderColor: highlightOnDrag && isDragActive ? '#c0ff02' : 'white' }
              : undefined,
          }}>
          <input {...getInputProps()} multiple={false} />
          <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
            <Typography variant="inherit">
              {isDragActive ? t('drop_files') : t('drag_and_drop_to_upload')}
            </Typography>
            <Typography variant="inherit">
              {`${tCommon('or')} `}
              <Link
                component="span"
                color="success2"
                fontWeight="regular"
                sx={{ cursor: 'pointer' }}>
                {t('browse_files')}
              </Link>
            </Typography>
          </Typography>
        </Stack>
      );
    }

    return <input {...getInputProps()} multiple={false} />;
  },
);

export default FilePicker;
